/**
 * Media Element Schema
 * Unified media handling for image, audio, and video
 */

import type { ElementSchema, MediaFieldSchema, TextFieldSchema } from '@ai-content-hub/content-kit';

export const defaultMediaElementI18n = {
    en: {
        uploadMedia: 'Upload media',
        altText: 'Alt text',
        upload: 'Upload',
        fromLink: 'Link',
        removeMedia: 'Remove',
        replaceMedia: 'Replace',
        maxSizeLabel: 'Max size:',
        dropZoneImage: 'Drop image here or click to upload',
        dropZoneAudio: 'Drop audio here or click to upload',
        dropZoneVideo: 'Drop video here or click to upload',
        enterImageUrl: 'Enter image URL...',
        enterAudioUrl: 'Enter audio URL...',
        enterVideoUrl: 'Enter video URL...',
        fileSizeExceedsMB: 'File size exceeds maximum ({mb}MB)',
        uploadFailedShort: 'Upload failed',
        invalidFileTypeFor: 'Invalid file type. Please select a {type} file.',
        ok: 'OK',
    },
    vi: {
        uploadMedia: 'Tải tệp media lên',
        altText: 'Văn bản thay thế',
        upload: 'Tải lên',
        fromLink: 'Liên kết',
        removeMedia: 'Xóa',
        replaceMedia: 'Thay thế',
        maxSizeLabel: 'Dung lượng tối đa:',
        dropZoneImage: 'Thả ảnh vào đây hoặc bấm để tải lên',
        dropZoneAudio: 'Thả âm thanh vào đây hoặc bấm để tải lên',
        dropZoneVideo: 'Thả video vào đây hoặc bấm để tải lên',
        enterImageUrl: 'Nhập URL ảnh...',
        enterAudioUrl: 'Nhập URL âm thanh...',
        enterVideoUrl: 'Nhập URL video...',
        fileSizeExceedsMB: 'Dung lượng vượt quá tối đa ({mb}MB)',
        uploadFailedShort: 'Tải lên thất bại',
        invalidFileTypeFor:
            'Định dạng tệp không hợp lệ. Vui lòng chọn tệp {type}.',
        ok: 'OK',
    },
};
export type MediaType = 'image' | 'audio' | 'video';

/** Media display fit options (CSS object-fit) */
export type MediaFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';

/** Media display configuration */
export interface MediaDisplayConfig {
    /** Object fit mode (default: 'contain') */
    fit?: MediaFit;
    /** Maximum width in pixels or CSS value (default: '100%') */
    maxWidth?: number | string;
    /** Maximum height in pixels or CSS value (default: 'auto') */
    maxHeight?: number | string;
    /** Fixed width (optional) */
    width?: number | string;
    /** Fixed height (optional) */
    height?: number | string;
    /** Enable cropping (for images) */
    crop?: boolean;
    /** Aspect ratio (e.g., '16:9', '4:3', '1:1') */
    aspectRatio?: string;
    /** Border radius in pixels or CSS value */
    borderRadius?: number | string;
    /** Background color (for letterboxing) */
    backgroundColor?: string;
}

/** Default display configuration */
export const DEFAULT_MEDIA_DISPLAY_CONFIG: MediaDisplayConfig = {
    fit: 'contain',
    maxWidth: '100%',
    maxHeight: 'auto',
};

/** Default max sizes per media type (in bytes) */
export const DEFAULT_MAX_SIZES: Record<MediaType, number> = {
    image: 10 * 1024 * 1024, // 10MB
    audio: 50 * 1024 * 1024, // 50MB
    video: 100 * 1024 * 1024, // 100MB
};

/** Default accepted MIME types per media type */
export const DEFAULT_ACCEPTED_TYPES: Record<MediaType, string[]> = {
    image: ['image/*'],
    audio: ['audio/*'],
    video: ['video/*'],
};

/** Accept strings for file input */
export const ACCEPT_STRINGS: Record<MediaType, string> = {
    image: 'image/*',
    audio: 'audio/*',
    video: 'video/*',
};

/** Media element data */
export interface MediaElementData {
    /** Media source URL */
    src: string;
    /** Media type */
    mediaType: MediaType;
    /** Title (for audio/video) or alt text (for images) */
    title?: string;
    /** Display configuration */
    displayConfig?: MediaDisplayConfig;
}

/** Default media element data */
export const defaultMediaData: MediaElementData = {
    src: '',
    mediaType: 'image',
    title: '',
    displayConfig: DEFAULT_MEDIA_DISPLAY_CONFIG,
};

/** Media element schema */
export const mediaElementSchema: ElementSchema<MediaElementData> = {
    type: 'element-media',
    title: 'Media',
    description: 'Upload or link to image, audio, or video content',
    version: '1.0.0',
    category: 'media',
    icon: 'Image',
    tags: ['media', 'image', 'audio', 'video', 'upload'],
    fields: [
        {
            name: 'src',
            type: 'media',
            label: 'Media Source',
            description: 'Upload or enter media URL',
            required: true,
            mediaType: 'image',
            accept: ['image/*', 'audio/*', 'video/*'],
        } as MediaFieldSchema,
        {
            name: 'title',
            type: 'text',
            label: 'Title / Alt Text',
            description: 'Description for accessibility',
            placeholder: 'Describe the media...',
        } as TextFieldSchema,
    ],
    defaultData: defaultMediaData,
    i18n: defaultMediaElementI18n,
};

/**
 * Convert CSS dimension value to string
 */
export function toCssValue(value: number | string | undefined): string | undefined {
    if (value === undefined) return undefined;
    if (typeof value === 'number') return `${value}px`;
    return value;
}

/**
 * Parse aspect ratio string to numeric ratio
 * e.g., '16:9' -> 16/9 = 1.777...
 */
export function parseAspectRatio(ratio: string | undefined): number | undefined {
    if (!ratio) return undefined;
    const parts = ratio.split(':');
    if (parts.length !== 2) return undefined;
    const width = parseFloat(parts[0]);
    const height = parseFloat(parts[1]);
    if (isNaN(width) || isNaN(height) || height === 0) return undefined;
    return width / height;
}

/**
 * Get CSS styles from display config
 */
export function getDisplayStyles(config: MediaDisplayConfig = {}): React.CSSProperties {
    const merged = { ...DEFAULT_MEDIA_DISPLAY_CONFIG, ...config };
    
    const styles: React.CSSProperties = {
        objectFit: merged.fit,
        maxWidth: toCssValue(merged.maxWidth),
        maxHeight: toCssValue(merged.maxHeight),
    };

    if (merged.width !== undefined) {
        styles.width = toCssValue(merged.width);
    }
    if (merged.height !== undefined) {
        styles.height = toCssValue(merged.height);
    }
    if (merged.borderRadius !== undefined) {
        styles.borderRadius = toCssValue(merged.borderRadius);
    }
    if (merged.backgroundColor) {
        styles.backgroundColor = merged.backgroundColor;
    }

    return styles;
}

/**
 * Get container styles for aspect ratio
 */
export function getAspectRatioStyles(config: MediaDisplayConfig = {}): React.CSSProperties | undefined {
    const ratio = parseAspectRatio(config.aspectRatio);
    if (!ratio) return undefined;

    return {
        aspectRatio: config.aspectRatio?.replace(':', '/'),
        width: toCssValue(config.width) || '100%',
        maxWidth: toCssValue(config.maxWidth),
        overflow: 'hidden',
    };
}

